import React, { useState, useEffect } from 'react';
import { CodeEditor } from '../components/CodeEditor';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Timer, Play, RefreshCw, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProgress } from '../hooks/useProgress';

interface Challenge {
  id: string;
  title: string;
  description: string;
  template: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  tests: { input: string; expected: string; }[];
  timeLimit: number;
}

const challenges: Challenge[] = [
  {
    id: '1',
    title: 'Two Sum',
    description: `Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target.
You may assume that each input would have exactly one solution, and you may not use the same element twice.
You can return the answer in any order.`,
    difficulty: 'Easy',
    template: `function twoSum(nums, target) {
  // Your solution here
}`,
    tests: [
      { input: '[2,7,11,15], 9', expected: '[0,1]' },
      { input: '[3,2,4], 6', expected: '[1,2]' },
    ],
    timeLimit: 300, // 5 minutes
  },
  // Add more challenges
];

export function SpeedrunPage() {
  const [currentChallenge, setCurrentChallenge] = useState(challenges[0]);
  const [code, setCode] = useState(currentChallenge.template);
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(currentChallenge.timeLimit);
  const [results, setResults] = useState<{ passed: boolean; message: string }[]>([]);
  const { updateTimeSpent } = useProgress();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning, timeLeft]);

  const startChallenge = () => {
    setIsRunning(true);
    setTimeLeft(currentChallenge.timeLimit);
    setCode(currentChallenge.template);
    setResults([]);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const runTests = () => {
    try {
      const fn = new Function('return ' + code)();
      const results = currentChallenge.tests.map(test => {
        try {
          const [nums, target] = test.input.split(',').map(s => JSON.parse(s.trim()));
          const result = JSON.stringify(fn(nums, target));
          const passed = result === test.expected;
          return {
            passed,
            message: passed 
              ? 'Test passed successfully!' 
              : `Expected ${test.expected} but got ${result}`
          };
        } catch (error) {
          return {
            passed: false,
            message: error instanceof Error ? error.message : 'Test failed'
          };
        }
      });
      setResults(results);

      // Update progress if all tests pass
      if (results.every(r => r.passed)) {
        updateTimeSpent(currentChallenge.id, currentChallenge.timeLimit - timeLeft);
      }
    } catch (error) {
      setResults([{
        passed: false,
        message: error instanceof Error ? error.message : 'Failed to run tests'
      }]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs 
          items={[
            { label: 'Practice', href: '/practice' },
            { label: 'Speedrun' }
          ]} 
        />

        <div className="mt-4 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentChallenge.title}
                </h1>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                  currentChallenge.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  currentChallenge.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {currentChallenge.difficulty}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-2 ${
                  timeLeft < 60 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  <Timer className="w-5 h-5" />
                  <span className="text-lg font-mono">{formatTime(timeLeft)}</span>
                </div>
                <button
                  onClick={isRunning ? runTests : startChallenge}
                  className={`px-4 py-2 text-white rounded-md flex items-center space-x-2 transition-colors ${
                    isRunning 
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isRunning ? (
                    <>
                      <Play className="w-5 h-5" />
                      <span>Run Tests</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      <span>Start Challenge</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="mt-4 prose max-w-none">
              <p className="text-gray-600">{currentChallenge.description}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            <div className="lg:col-span-2 h-[600px]">
              <CodeEditor
                code={code}
                onChange={setCode}
              />
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
                Test Results
              </h3>
              <AnimatePresence>
                {results.map((result, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`p-4 rounded-lg ${
                      result.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    {result.passed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 mb-2" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mb-2" />
                    )}
                    <p className={result.passed ? 'text-green-700' : 'text-red-700'}>
                      {result.message}
                    </p>
                  </motion.div>
                ))}
              </AnimatePresence>
              {!isRunning && results.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Start the challenge to see test results</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}